import { useEffect, useRef, useState } from 'react';
import useFocusTrap from '../useFocusTrap';
import ErrorBoundary from '../ErrorBoundary';
import '../design/atlasVivo/Buttons.css';
import '../design/atlasVivo/AtlasVivoShell.css';
import './AtlasVivoLayout.css';
import { WORKSPACE_VIEWS } from '../features/workspaceState';
import AtlasIcon from '../design/atlasVivo/AtlasIcon';
import AtlasScopeBar from '../design/atlasVivo/AtlasScopeBar';
import AtlasLensBar from '../design/atlasVivo/AtlasLensBar';
import AtlasContextPanel from '../design/atlasVivo/AtlasContextPanel';
import PulseRail from '../design/atlasVivo/PulseRail';
import DiaWorkspace from '../design/atlasVivo/DiaWorkspace';
import HistoriasWorkspace from '../design/atlasVivo/HistoriasWorkspace';
import AtlasEmptyState from '../design/atlasVivo/AtlasEmptyState';
import OnboardingChecklist from '../design/atlasVivo/OnboardingChecklist';
import { ONBOARDING_COPY } from '../design/atlasVivo/onboardingCopy';
import WorkspaceStatus from '../design/atlasVivo/WorkspaceStatus';
import { t } from '../i18n';
import '../design/atlasVivo/tokens.css';
import {
  mountNediTheme,
  NEDI_CONTRACT_VERSION,
} from '../design/nedi/mountNediTheme';

function getViewMeta() {
  return {
    atlas: { icon: 'map', label: t('tab.atlas') },
    dia: { icon: 'calendar-days', label: t('tab.dia') },
    historias: { icon: 'sparkles', label: t('tab.historias') },
    dados: { icon: 'database', label: t('tab.dados') },
  };
}

function getWorkspaceErrorCopy(workspace) {
  const map = {
    atlas: {
      title: t('web.layout.error.atlas.title'),
      message: t('web.layout.error.atlas.message'),
    },
    dia: {
      title: t('web.layout.error.dia.title'),
      message: t('web.layout.error.dia.message'),
    },
    historias: {
      title: t('web.layout.error.historias.title'),
      message: t('web.layout.error.historias.message'),
    },
    dados: {
      title: t('web.layout.error.dados.title'),
      message: t('web.layout.error.dados.message'),
    },
  };
  return map[workspace] ?? map.atlas;
}

function WorkspaceErrorBoundary({ workspace, children }) {
  const copy = getWorkspaceErrorCopy(workspace);
  return (
    <ErrorBoundary title={copy.title} message={copy.message} compact>
      {children}
    </ErrorBoundary>
  );
}

// Layout do overhaul Atlas Vivo (docs/plans/overhaul-ux-atlas-vivo.md).
// Recebe os MESMOS layoutProps que WebLayout/DesktopLayout — inclusive o
// mapPanel real (com <div id="map">), o filterDrawer e os modais. O mapa
// inicializa igual: o efeito de init em App.js só procura getElementById('map'),
// não importa qual layout o renderiza.
//
// Fase 8: skin nativa (docs/design/atlas-vivo/styles.css) substitui o antigo
// TopBar reaproveitado cru por AtlasScopeBar; o mapa continua o mesmo real.
function AtlasVivoLayout({
  target = 'web',
  view: controlledView,
  onViewChange,
  mapPanel,
  filterDrawer,
  globalSearchPalette,
  globalLoadingOverlay,
  modals,
  lensProps,
  atlasContext,
  pulseRail,
  diaProps,
  historiasProps,
  dadosProps,
  scopeBar,
  headline,
  datasetEmpty = false,
  onGoToImport,
  loading,
  error,
  onRetry,
  offline,
  offlineCachedAt,
}) {
  const [internalView, setInternalView] = useState('atlas');
  const view = controlledView ?? internalView;
  const setView = (nextView) => {
    if (onViewChange) onViewChange(nextView);
    else setInternalView(nextView);
  };
  const [lensOpen, setLensOpen] = useState(false);
  const [contextLiveMessage, setContextLiveMessage] = useState('');
  const prevSelectionRef = useRef(atlasContext?.selection ?? null);
  const prevRailWorkspaceRequestRef = useRef(pulseRail?.workspaceRequest ?? 0);
  const lensPanelRef = useFocusTrap(lensOpen, () => setLensOpen(false));

  useEffect(() => {
    mountNediTheme();
  }, []);

  useEffect(() => {
    const selection = atlasContext?.selection ?? null;
    const previous = prevSelectionRef.current;
    if (!selection && previous) {
      setContextLiveMessage('Painel contextual fechado');
    } else if (selection && !previous) {
      const title = selection.kind === 'concert'
        ? (selection.data?.artist_name || 'Show')
        : (selection.data?.venue || 'Lugar');
      setContextLiveMessage(`Painel contextual aberto: ${title}`);
    }
    prevSelectionRef.current = selection;
  }, [atlasContext?.selection]);

  useEffect(() => {
    const request = pulseRail?.workspaceRequest ?? 0;
    if (request > prevRailWorkspaceRequestRef.current) {
      setView('dia');
      prevRailWorkspaceRequestRef.current = request;
    }
  }, [pulseRail?.workspaceRequest]);

  const ENABLED_VIEWS = ['atlas', 'dia', 'historias', 'dados'];
  // Render em função pra reusar os mesmos botões no dock (desktop) e nas bottom
  // tabs (mobile) — sem compartilhar instâncias de elemento entre pais.
  const renderNavButtons = () =>
    WORKSPACE_VIEWS.map((workspace) => {
      const disabled = !ENABLED_VIEWS.includes(workspace);
      return (
        <button
          key={workspace}
          type="button"
          className={`av-dock-btn${workspace === view ? ' is-active' : ''}`}
          aria-current={workspace === view ? 'page' : undefined}
          aria-disabled={disabled || undefined}
          title={getViewMeta()[workspace].label}
          onClick={() => !disabled && setView(workspace)}
        >
          <AtlasIcon name={getViewMeta()[workspace].icon} size={19} />
          <span>{getViewMeta()[workspace].label}</span>
        </button>
      );
    });

  return (
    <div
      className={`atlas-vivo-scope av-shell av-layout av-target-${target}`}
      data-layout-target={target}
      data-atlas-view={view}
      data-nedi-contract={NEDI_CONTRACT_VERSION}
    >
      <div className="av-sr-only" aria-live="polite" role="status" aria-label={t('web.layout.contextStatus')}>
        {contextLiveMessage}
      </div>

      <nav className="av-dock" aria-label={t('web.layout.nav.a11y')}>
        <div className="av-brand" aria-hidden="true">
          <span>P</span>
        </div>
        {renderNavButtons()}
      </nav>

      <div className="av-shell-main">
        <header className="av-scopebar av-layout-scopebar">
          {scopeBar ? (
            <AtlasScopeBar
              currentFilters={scopeBar.currentFilters}
              onFilterChange={scopeBar.onFilterChange}
              onOpenSearch={scopeBar.onOpenSearch}
              user={scopeBar.user}
              onLogout={scopeBar.onLogout}
              offline={scopeBar.offline ?? offline}
              offlineCachedAt={scopeBar.offlineCachedAt ?? offlineCachedAt}
              canInstall={scopeBar.canInstall}
              onInstallApp={scopeBar.onInstallApp}
              periodEmpty={scopeBar.periodEmpty}
              loadedData={scopeBar.loadedData}
            />
          ) : null}
        </header>

        <div className="av-shell-body">
          <div className="av-canvas">
            <div className="av-canvas-map av-layout-canvas-map">
              {mapPanel}

              {view === 'atlas' && (
                <WorkspaceErrorBoundary workspace="atlas">
                  <>
                    <div className="av-map-shade" aria-hidden="true" />
                    {datasetEmpty ? (
                      <div className="av-map-empty">
                        <AtlasEmptyState
                          {...ONBOARDING_COPY.atlasEmpty}
                          actionLabel={onGoToImport ? ONBOARDING_COPY.atlasEmpty.cta : undefined}
                          onAction={onGoToImport}
                        />
                      </div>
                    ) : headline ? (
                      <div className="av-map-title">
                        <span className="av-eyebrow">{headline.eyebrow}</span>
                        <h1>{headline.title}</h1>
                        <p>{headline.subtitle}</p>
                      </div>
                    ) : null}
                    {datasetEmpty && onGoToImport ? (
                      <OnboardingChecklist onImport={onGoToImport} />
                    ) : null}
                  </>
                </WorkspaceErrorBoundary>
              )}

              {view === 'atlas' && lensProps && (
                <AtlasLensBar {...lensProps} onOpenAdvanced={() => setLensOpen(true)} />
              )}

              {lensOpen && (
                <div
                  ref={lensPanelRef}
                  className="av-lens-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('web.lenses.panel.a11y')}
                >
                  <div className="av-lens-panel-header">
                    <strong>{t('web.lenses.title')}</strong>
                    <button type="button" className="av-icon-btn" onClick={() => setLensOpen(false)} aria-label={t('web.layout.closeLenses')}>
                      <AtlasIcon name="x" size={16} />
                    </button>
                  </div>
                  <div className="av-lens-panel-body">{filterDrawer}</div>
                </div>
              )}

              {view === 'dia' && diaProps && (
                <WorkspaceErrorBoundary workspace="dia">
                  <WorkspaceStatus
                    loading={loading && diaProps.dayEvents.length === 0}
                    error={error}
                    onRetry={onRetry}
                    offline={offline}
                    offlineCachedAt={offlineCachedAt}
                  >
                    <DiaWorkspace
                      selectedDate={diaProps.selectedDate}
                      dayEvents={diaProps.dayEvents}
                      eventDays={diaProps.eventDays}
                      onSelectDay={diaProps.onSelectDay}
                      onSelectEvent={diaProps.onSelectEvent}
                      onImportData={onGoToImport}
                    />
                  </WorkspaceStatus>
                </WorkspaceErrorBoundary>
              )}

              {view === 'historias' && historiasProps && (
                <WorkspaceErrorBoundary workspace="historias">
                  <WorkspaceStatus
                    loading={loading && historiasProps.stories.length === 0}
                    error={error}
                    onRetry={onRetry}
                    offline={offline}
                    offlineCachedAt={offlineCachedAt}
                    empty={!loading && historiasProps.stories.length === 0 && !error && !offlineCachedAt}
                    emptyTitle={ONBOARDING_COPY.historiasEmpty.title}
                    emptyMessage={ONBOARDING_COPY.historiasEmpty.message}
                    emptyCta={ONBOARDING_COPY.historiasEmpty.cta}
                    onImport={onGoToImport}
                  >
                    <HistoriasWorkspace
                      stories={historiasProps.stories}
                      activeStoryKey={historiasProps.activeStoryKey}
                      onActiveStoryChange={historiasProps.onActiveStoryChange}
                      resetToken={historiasProps.resetToken}
                      onImportData={onGoToImport}
                    />
                  </WorkspaceStatus>
                </WorkspaceErrorBoundary>
              )}

              {view === 'dados' && dadosProps && (
                <WorkspaceErrorBoundary workspace="dados">
                  <WorkspaceStatus
                    loading={loading && dadosProps.stories.length === 0}
                    error={error}
                    onRetry={onRetry}
                    offline={offline}
                    offlineCachedAt={offlineCachedAt}
                  >
                    <HistoriasWorkspace
                      stories={dadosProps.stories}
                      eyebrow="DADOS"
                      title="Dados"
                      subtitle="{t('web.dados.maintenance.subtitle')}"
                      ariaLabel="Workspace Dados"
                      activeStoryKey={dadosProps.activeStoryKey}
                      onActiveStoryChange={dadosProps.onActiveStoryChange}
                      resetToken={dadosProps.resetToken}
                    />
                  </WorkspaceStatus>
                </WorkspaceErrorBoundary>
              )}
            </div>
          </div>

          {view === 'atlas' && atlasContext && (
            <AtlasContextPanel
              selection={atlasContext.selection}
              onClose={atlasContext.onClose}
              onEditTags={atlasContext.onEditTags}
            />
          )}
        </div>

        <footer className="av-pulserail">
          {pulseRail && pulseRail.model ? (
            <PulseRail
              model={pulseRail.model}
              summary={pulseRail.summary}
              onSelectPosition={pulseRail.onSelectPosition}
              onHoverPosition={pulseRail.onHoverPosition}
              onImportData={onGoToImport}
            />
          ) : (
            <span className="av-pulserail-empty" role="status">
              {t('web.onboarding.pulseEmpty.message')}
            </span>
          )}
        </footer>

        <nav className="av-bottom-tabs" aria-label={t('web.layout.nav.mobile.a11y')}>
          {renderNavButtons()}
        </nav>
      </div>

      {globalSearchPalette}
      {globalLoadingOverlay}
      {modals}
    </div>
  );
}

export default AtlasVivoLayout;
